import {
  ItemIssueSelfServe,
  type ItemIssueSelfServeCode,
  type SswBoxError,
  type SswUnitIssue,
} from '@hungryroot/rest';

const ISSUE_TYPE_LABELS: Record<ItemIssueSelfServeCode, string> = {
  [ItemIssueSelfServe.Damaged]:
    'Damaged item(s)  (physical damage to packaging)',
  [ItemIssueSelfServe.Missing]:
    'Missing item(s)  (item on the order slip did not arrive)',
  [ItemIssueSelfServe.Spoiled]: 'Spoiled item(s)  (past its freshness)',
  [ItemIssueSelfServe.Other]:
    'Other (e.g. temperature issue, missing delivery, broken icepack, etc.)',
};

export const ISSUE_TYPE_OPTIONS = (
  Object.values(ItemIssueSelfServe) as ItemIssueSelfServeCode[]
).map((value) => ({
  value: String(value),
  label: ISSUE_TYPE_LABELS[value],
}));

export function getIssueTypeShortLabel(
  issue: ItemIssueSelfServeCode | null
): string {
  if (!issue) {
    return '';
  }

  return ISSUE_TYPE_LABELS[issue].split('  (')[0].toLowerCase();
}

const formatDollars = (amount: number) => `$${amount.toFixed(2)}`;

export const WIZARD_COPY = {
  issue_type: {
    title: "We're here to help!",
    subtext: 'Did you have any of these issues with your delivery?',
  },
  select_items: {
    title: 'So sorry about that!',
    getSubtext: (issue: ItemIssueSelfServeCode | null) =>
      `Please select the ${getIssueTypeShortLabel(issue)}.`,
  },
  credit_offer: {
    title: 'Can we make this up to you?',
    getSubtext: (amount: number) =>
      `We'd love to make this right by adding ${formatDollars(
        amount
      )} to your Hungryroot account.\nIf you'd like to connect with Customer Care, we'll look into this further and follow up with you ASAP.`,
    acceptLabel: (amount: number) =>
      `Yes, add ${formatDollars(amount)} to my account to solve this issue`,
    declineLabel: "No, I'd like to talk to Customer Care",
  },
  credit_confirm: {
    getTitle: (firstName: string) => `All set, ${firstName}!`,
    getSubtext: (amount: number) =>
      `Nice! Your ${formatDollars(
        amount
      )} will be automatically applied to your next delivery.`,
  },
  free_text: {
    title: 'We want you to have the best experience possible.',
    subtext:
      'Click “next” to get in touch with our Customer Care team. If you’d like to include any other information, feel free to add it below.',
    otherTitle: 'Can you tell us a bit more?',
    otherSubtext:
      "We'd love to know the details so we can improve your experience.",
    placeholder: "Tell us as much or as little as you'd like.",
  },
  care_confirm: {
    title: "We'll send you an email soon!",
    getSubtext: (firstName: string) =>
      `Our Customer Care team will get back to you via email ASAP. Thanks for your patience, ${firstName}!`,
  },
  error: {
    title: 'Oh no! Something went wrong.',
    subtext:
      'Please reach out to support@hungryroot.com. We could not process your request.',
  },
} as const;

/** Allowed characters for free-text feedback (legacy parity). */
export const FREE_TEXT_ALLOWED_PATTERN = /^[\r\na-zA-Z0-9,.!'&#@*()?"$/ ]*$/;

export function getFreeTextInvalidCharacterMessage(char: string): string {
  const display = char === ' ' ? '" "' : `"${char}"`;

  return `${display} isn't supported`;
}

// v1/v2 wizard (Self Serve Widget Updates, per-product flow)

export const UNIT_ISSUE_LABELS: Record<SswUnitIssue, string> = {
  missing: 'Missing',
  spoiled: 'Spoiled',
  damaged: 'Damaged',
};

export const BOX_ERROR_LABELS: Record<SswBoxError, string> = {
  damaged_box: 'Damaged',
  warm_box: 'Warm',
};

export const SSW_WIZARD_COPY = {
  issue_select: {
    title: 'What went wrong?',
    neverArrived: 'Order never arrived',
    wholeBox: 'Whole order was damaged or warm',
    itemIssue: 'Some items were missing, damaged or spoiled',
    somethingElse: 'I need help with something else',
  },
  select_items: {
    title: 'Which items had issues?',
    issueRequired: 'Issue type is required',
  },
  whole_box_issue: {
    title: 'What was the issue with your box?',
  },
  credit_offer: {
    title: 'Can we make this up to you?',
    declineLabel: "No, I'd like to talk to Customer Care",
  },
  on_its_way: {
    title: 'Your order is on its way!',
    subtext:
      "The carrier hasn't marked your order as delivered yet. They sometimes run behind, and your order may still show up today. If you'd still like to reach out, our customer care team is here to help.",
    careLabel: "I'd like to talk to customer care.",
  },
  escalate: {
    title: 'We want you to have the best experience possible',
    subtext:
      "Click “Next” to get in touch with our Customer Care Team. They'll get the information you've provided so far.",
    /** Only when the escalation is a ticket, which carries the text. */
    addDetail: "If you'd like to add any additional detail, enter it below.",
  },
} as const;
